import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Instagram, Heart } from "lucide-react";
import { useSavedMatches } from "@/hooks/useSavedMatches";

const links = [
  { to: "/", label: "Home" },
  { to: "/drink-finder", label: "Drink Finder" },
  { to: "/cocktails", label: "Cocktails" },
  { to: "/bar-cart", label: "Bar Cart" },
  { to: "/learn", label: "Learn" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Brand Collabs" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { saved } = useSavedMatches();
  return (
    <header className="sticky top-0 z-40 bg-ink/85 backdrop-blur-md border-b border-cream/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-baseline gap-2" data-testid="brand-logo">
          <span className="font-serif-display text-2xl sm:text-3xl font-bold tracking-tight text-cream">Sip</span>
          <span className="font-serif-editor italic text-2xl sm:text-3xl font-medium text-pink-brand">Alchemy</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-xs font-semibold tracking-[0.15em] uppercase transition-colors ${isActive ? "text-pink-brand" : "text-cream/85 hover:text-pink-brand"}`
              }
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/saved"
            aria-label="Saved cocktails"
            className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-cream/20 text-cream hover:bg-pink hover:text-cream hover:border-pink transition-colors"
            data-testid="header-saved"
          >
            <Heart size={16} fill={saved.length > 0 ? "currentColor" : "none"} />
            {saved.length > 0 && (
              <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] font-bold rounded-full bg-pink text-cream grid place-items-center" data-testid="saved-count">
                {saved.length}
              </span>
            )}
          </Link>
          <a
            href="https://instagram.com/sip_alchemy_"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-full border border-cream/20 text-cream hover:bg-pink hover:text-cream hover:border-pink transition-colors"
            data-testid="header-instagram"
          >
            <Instagram size={18} />
          </a>
          <button
            className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full bg-ink text-cream"
            onClick={() => setOpen(v => !v)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-cream/10 bg-ink" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col">
            {links.map(l => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-3 border-b border-cream/10 text-lg font-serif-editor ${isActive ? "text-pink-brand" : "text-cream"}`
                }
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href="https://instagram.com/sip_alchemy_"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 btn-pill btn-pink"
              data-testid="mobile-instagram-link"
            >
              <Instagram size={18} /> @sip_alchemy_
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
